import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'

import { AuthProvider } from '@/lib/auth'
import { ThemeProvider } from '@/lib/theme'
import { ToastProvider } from '@/components/ui/Toast'
import { ApiError } from '@/lib/api'
import { AppRoutes } from '@/routes/AppRoutes'

import '@/styles/tokens.css'
import '@/styles/global.css'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      /**
       * *** ADMIN DATA IS SHARED AND CHANGES UNDER YOU ***
       * Two admins may work at once, and mentors change their own profiles.
       * Thirty seconds is short enough that a stale list is rare, long enough
       * that clicking between screens does not refetch everything.
       */
      staleTime: 30_000,

      // Refetching when the window regains focus is right for a tool someone
      // leaves open across a workday and returns to.
      refetchOnWindowFocus: true,

      retry: (failureCount, error) => {
        /*
          Retrying a 4xx is pointless and harmful: a 401 would burn refresh
          attempts, and a 403 or 404 will never become a 200. Only transient
          failures — 5xx and rate limits — are worth another try.
        */
        if (error instanceof ApiError && !error.isTransient) return false
        return failureCount < 2
      },
    },
    mutations: {
      // A mutation is a deliberate action. Retrying it automatically risks
      // performing it twice — creating two mentors, sending two resets.
      retry: false,
    },
  },
})

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <ToastProvider>
          <AuthProvider>
            <AppRoutes />
          </AuthProvider>
        </ToastProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </StrictMode>,
)
